import { ReactNode } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faArrowRotateLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ButtonLink from '@v2/shared/components/ButtonLink';
import ContentCard from '@v2/shared/components/ContentCard';
import FaqAccordion from '@v2/shared/components/FaqAccordion';
import PageHeading from '@v2/shared/components/PageHeading';
import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { liveAgentHours } from '@v2/shared/lib/supportHours';

/**
 * The date the page states, which is the date this wording ships. It is copy, not a
 * render-time value: `new Date()` here would move the date on every deploy and on every
 * cache miss, telling a visitor the policy changed on a day it did not. Change it by
 * hand, in the same commit that changes the words above it.
 */
const LAST_UPDATED = 'September 22, 2026';

/**
 * Headings, sized once rather than at twenty call sites.
 *
 * Margins are longhands on purpose. `prose` compiles to `margin-top: ... !important`
 * inside `.v2-root`, so the `m-0` shorthand silently loses to it while `mt-0` wins —
 * see `postcss/beatLegacyImportant.js`.
 */
const H2 = 'mb-0 mt-0 text-2xl font-extrabold tracking-tight';
const H3 = 'mb-1.5 mt-6 text-xl font-extrabold';
const TAGLINE = 'mb-4 mt-1 text-sm text-slate-500';

/**
 * What an anchored section has to clear to land below the fixed header, which is three
 * different heights — 53px on a phone, 126px in the middle, 82px wide. Same trio the
 * Magzter Gold pricing panel already uses for the same reason.
 */
const ANCHOR_OFFSET = 'scroll-mt-14 md:scroll-mt-32 xl:scroll-mt-24';

/** The four sections a visitor arrives wanting, and the number each of them turns on. */
const GLANCE = [
  {
    href: '#magazines',
    title: 'Magazines',
    summary: <>Pro-rated refund on unsent issues, up to <b>6 months</b> from purchase</>,
  },
  {
    href: '#books',
    title: 'Books & other goods',
    summary: <>Full refund when returned within <b>30 days</b> of delivery</>,
  },
  {
    href: '#magzter',
    title: 'Magzter Gold',
    summary: <><b>7-day</b> no-questions-asked guarantee on every billing</>,
  },
  {
    href: '#zinio',
    title: 'ZINIO Unlimited',
    summary: <>Cancel anytime, <b>no refunds</b> once billed</>,
  },
];

/** The contents list. Every entry is a card below, in the order they appear. */
const SECTIONS = [
  { href: '#cancel', label: 'Cancel vs. stop renewal' },
  { href: '#magazines', label: 'Magazines' },
  { href: '#zinio', label: 'ZINIO Unlimited' },
  { href: '#magzter', label: 'Magzter Gold' },
  { href: '#books', label: 'Books & Goods' },
  { href: '#payment', label: 'How refunds are paid' },
  { href: '#request', label: 'How to request' },
  { href: '#faqs', label: 'FAQs' },
];

/** The three numbers the magazine policy comes down to. */
const MAGAZINE_FACTS = [
  { key: 'Refund window', value: '6 months', note: 'from the order date' },
  {
    key: "What's refunded",
    value: 'Unsent issues',
    note: 'pro-rated, to your original payment method',
  },
  {
    key: "When you'll see it",
    value: '5–7 business days',
    note: 'we process within 48 hours; your bank does the rest',
  },
];

/**
 * The reason chart. Every row answers the same way on purpose — that *is* the policy, and
 * a visitor who came to find their own reason should be able to see that it does not
 * change the answer.
 */
const REASONS = [
  'Changed my mind, or any other reason',
  'Magazine stopped publishing',
  'Magazine paused production',
  'Magazine changed frequency',
  'Magazine went digital-only',
];

/** A muted caps label over a big number — the `.fact` and `.opt` label treatment. */
const MICRO_LABEL = 'text-[11px] font-bold uppercase tracking-wider';

function Pill({ children }: { children: ReactNode }) {
  return (
    <p
      className={cn(
        'not-prose mb-2.5 mt-0 inline-block rounded-full px-2.5 py-1',
        'bg-primary-50 text-primary',
        MICRO_LABEL
      )}
    >
      {children}
    </p>
  );
}

/** A tinted panel for an aside that is still running copy — the `.note` treatment. */
function Note({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'rounded-lg border border-solid border-slate-200 bg-slate-50 px-4 py-3.5 text-sm',
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * A numbered procedure. `not-prose` because the numbers are drawn rather than listed:
 * prose would style its own counters underneath these and indent the text away from
 * them.
 */
function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="not-prose m-0 mt-4 list-none p-0">
      {items.map((item, index) => (
        <li key={index} className="mb-4 flex gap-4 last:mb-0">
          <span
            className={cn(
              'flex h-8 w-8 flex-none items-center justify-center rounded-full',
              'bg-primary text-sm font-extrabold text-white'
            )}
          >
            {index + 1}
          </span>
          <span className="text-slate-700">{item}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * /refund.
 *
 * A rewrite of the policy page, to the design in the task's mockup and in the visual
 * language /reviews established: the same gradient heading band, the same bordered band
 * of figures pulled up over it, the same cards, and the same accordion for the FAQs. The
 * two pages are meant to read as a set.
 *
 * The copy is no longer the copy `includes/modules/legal/refund.html` held — it said the
 * same thing in different words, and it is gone: it rendered only in the checkout
 * lightbox, and the checkout agreement links to this page instead now. The policy itself
 * never changed, and every window in here (6 months, 30 days, 7 days) is the window that
 * module stated. This component is the one published copy.
 *
 * Structure worth keeping: each card is its own anchor, because the contents list and the
 * band of figures at the top both link into them, and a page this long is otherwise a
 * scroll. `prose` goes on the cards that hold running copy rather than on a wrapper
 * around all of them, so the FAQ accordion and the structured panels are not restyled by
 * it.
 *
 * Nothing here varies by visitor, which is what lets the template cache the render for a
 * day. Keep it that way.
 */
export default function RefundPolicy() {
  const { buildUrl } = useUrlGenerator();

  // Hand-written paths, so they go through buildUrl: the AU storefront serves from a
  // base directory and a bare path drops its visitors onto the .com site.
  const discountLockUrl = buildUrl('/my-account/discountlock');
  const ordersUrl = buildUrl('/customer?xCmd=orders');
  const guaranteeUrl = buildUrl('/guarantee');

  // Each link carries the reason it is actually about, so the message lands on the right
  // Kustomer queue with the right ticket title instead of arriving as a book enquiry.
  // The number is the subject id -- see TicketSubjectListBuilder.
  const contactBooksUrl = buildUrl('/contact?xSubject=34');
  const contactCancelUrl = buildUrl('/contact?xSubject=24');
  const contactBereavementUrl = buildUrl('/contact?xSubject=38');

  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Refund Policy' }]} />

      <PageHeading
        eyebrow="Refund &amp; Return Policy"
        // A deeper floor so the "Last updated" line clears the figures strip below, which is
        // pulled 24px up over the band: on PageHeading's own `md:py-6` the two met exactly.
        //
        // `md:pb-14` is not a duplicate of `pb-14`: Tailwind emits every responsive variant
        // after the whole base layer, so a base `pb-14` loses to `md:py-6` from md up. Same
        // reason /shipping and /affiliate-program write theirs in pairs.
        className="pb-14 md:pb-14"
        intro={
          <>
            Most magazine orders can be cancelled for a pro-rated refund within 6 months of
            purchase. Digital plans, books and other goods have their own rules, and every one of
            them is spelled out below. This page is our{' '}
            <a href={guaranteeUrl} className="text-white underline underline-offset-2">
              satisfaction guarantee
            </a>{' '}
            in practice.
            <span className="mt-3.5 block text-xs opacity-75">Last updated {LAST_UPDATED}</span>
          </>
        }
      >
        Refunds, made simple
      </PageHeading>

      {/* The figures, pulled up over the band above them the way /reviews pulls its
          rating card up over its hero. `relative` so the overlap is this one on top. */}
      <ul
        className={cn(
          'relative z-10 mx-3 -mt-6 grid list-none grid-cols-2 p-0 md:mx-5 md:grid-cols-4',
          'overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-sm'
        )}
      >
        {GLANCE.map((item, index) => (
          <li
            key={item.href}
            className={cn(
              'border-0 border-solid border-slate-200',
              // Dividers between cells: two columns on a phone, four above it.
              index % 2 === 1 && 'border-l',
              index >= 2 && 'border-t md:border-t-0',
              index > 0 && 'md:border-l'
            )}
          >
            <a href={item.href} className="block h-full px-4 py-3.5 no-underline hover:bg-slate-50">
              <span className="block text-sm font-extrabold text-slate-900">{item.title}</span>
              <span className="mt-0.5 block text-xs leading-snug text-slate-500 [&>b]:text-primary">
                {item.summary}
              </span>
            </a>
          </li>
        ))}
      </ul>

      <nav aria-label="On this page" className="mt-6 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold text-slate-500">On this page:</span>
        {SECTIONS.map((section) => (
          <a
            key={section.href}
            href={section.href}
            className={cn(
              'rounded-full border border-solid border-slate-200 px-3.5 py-1',
              'font-semibold text-slate-700 no-underline',
              'hover:border-primary hover:text-primary'
            )}
          >
            {section.label}
          </a>
        ))}
      </nav>

      <div className="mt-5 flex flex-col gap-6">
        <ContentCard id="cancel" className={cn('prose', ANCHOR_OFFSET)}>
          <Pill>Start here</Pill>

          <h2 className={H2}>Cancelling? Pick the option that matches what you want.</h2>

          <p className={TAGLINE}>
            &quot;Cancel&quot; can mean two different things. Choosing the right one keeps you from
            cutting short a subscription you already paid for.
          </p>

          {/* Two panels rather than two paragraphs: the whole point of the section is that
              these are alternatives, and a visitor has to be able to tell at a glance
              which one they are reading. */}
          <div className="not-prose grid gap-4 md:grid-cols-2">
            <div className="flex flex-col rounded-lg border border-solid border-slate-200 p-5">
              <p className={cn('m-0 text-primary', MICRO_LABEL)}>Option 1</p>
              <h3 className="mb-2 mt-1 text-lg font-extrabold text-slate-900">
                Stop future renewals, keep my issues
              </h3>
              <ul className="mb-4 ml-0 list-disc pl-5 text-sm text-slate-700 marker:text-primary">
                <li className="mb-1.5">
                  Turns off DiscountLock auto-renewal for that subscription.
                </li>
                <li className="mb-1.5">
                  Your subscription continues through the end of its current term. Nothing is
                  refunded because nothing is cancelled.
                </li>
                <li className="mb-1.5">
                  Takes about a minute in your account. No need to contact us.
                </li>
                <li>
                  DiscountLock can&apos;t be turned back on once it&apos;s off. To renew later,
                  place a new order.
                </li>
              </ul>
              <ButtonLink
                href={discountLockUrl}
                className={cn(
                  'mt-auto self-start border border-solid border-primary',
                  'bg-white normal-case tracking-normal text-primary hover:bg-primary-50'
                )}
              >
                Manage DiscountLock
              </ButtonLink>
            </div>

            <div
              className={cn(
                'flex flex-col rounded-lg border border-solid p-5',
                'border-primary-100 bg-primary-50'
              )}
            >
              <p className={cn('m-0 text-primary', MICRO_LABEL)}>Option 2</p>
              <h3 className="mb-2 mt-1 text-lg font-extrabold text-slate-900">
                Cancel now and get a refund
              </h3>
              <ul className="mb-4 ml-0 list-disc pl-5 text-sm text-slate-700 marker:text-primary">
                <li className="mb-1.5">
                  Ends the subscription and refunds the issues that haven&apos;t been sent yet.
                </li>
                <li className="mb-1.5">
                  Available within 6 months of the order date (for a renewal, within 6 months of
                  the renewal charge).
                </li>
                <li className="mb-1.5">
                  If a renewal hasn&apos;t processed yet, cancelling before the process date means a
                  full refund of that renewal.
                </li>
                <li>
                  Turning off DiscountLock alone does not cancel or refund the current order.
                </li>
              </ul>
              <ButtonLink
                href={ordersUrl}
                className="mt-auto self-start normal-case tracking-normal"
              >
                Cancel from My Orders
              </ButtonLink>
              <p className="mb-0 mt-2 text-xs text-slate-500">
                Can&apos;t sign in? <a href={contactCancelUrl}>Contact Customer Care</a> and
                we&apos;ll handle it.
              </p>
            </div>
          </div>
        </ContentCard>

        <ContentCard id="magazines" className={cn('prose', ANCHOR_OFFSET)}>
          <h2 className={H2}>Print &amp; digital magazine subscriptions</h2>
          <p className={TAGLINE}>Any reason, or no reason at all, for 6 full months.</p>

          <div className="not-prose grid gap-3 md:grid-cols-3">
            {MAGAZINE_FACTS.map((fact) => (
              <div
                key={fact.key}
                className="rounded-lg border border-solid border-slate-200 bg-slate-50 px-3.5 py-3"
              >
                <p className={cn('m-0 text-slate-500', MICRO_LABEL)}>{fact.key}</p>
                <p className="m-0 mt-0.5 text-lg font-extrabold tabular-nums text-slate-900">
                  {fact.value}
                </p>
                <p className="m-0 mt-0.5 text-xs leading-snug text-slate-500">{fact.note}</p>
              </div>
            ))}
          </div>

          <h3 className={H3}>What &quot;pro-rated&quot; means</h3>
          <p>
            You&apos;re refunded for the issues that haven&apos;t been sent yet. Issues already
            mailed to you (or already in production with the publisher) aren&apos;t refundable.
          </p>

          <div className="not-prose rounded-r-lg border-0 border-l-4 border-solid border-primary bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <b className="text-slate-900">Example:</b> a 12-issue subscription for $24.00 works out
            to $2.00 per issue.
            <dl className="m-0 mt-1.5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 tabular-nums">
              <dt className="m-0">Issues already sent</dt>
              <dd className="m-0">4</dd>
              <dt className="m-0">Issues remaining</dt>
              <dd className="m-0">8</dd>
              <dt className="m-0 font-bold text-slate-900">Your refund</dt>
              <dd className="m-0 font-bold text-slate-900">8 × $2.00 = $16.00</dd>
            </dl>
          </div>

          <h3 className={H3}>Does the reason matter?</h3>
          <p>
            No. Inside the 6-month window every reason gets the same pro-rated refund, whether the
            magazine stopped publishing, changed how often it comes, went digital-only, paused
            production, or you simply changed your mind. After 6 months, orders are final for every
            reason.
          </p>

          {/* The chart scrolls rather than squeezing on a narrow screen, and the wrapper is
              what scrolls: `display:block` on the table itself is the usual shortcut and
              costs the element its table semantics in some screen readers. It takes a
              tabindex because a region that scrolls has to scroll without a mouse, and the
              table a floor, or the columns keep shrinking and there is nothing to scroll. */}
          <div className="not-prose my-4 w-full overflow-x-auto" tabIndex={0}>
            <table className="w-full min-w-[520px] border-collapse text-left text-sm text-slate-700">
              <thead>
                <tr>
                  <th
                    className={cn(
                      'border-0 border-b border-solid border-slate-200 bg-slate-50',
                      'px-3 py-2.5 text-left align-top text-slate-500',
                      MICRO_LABEL
                    )}
                  >
                    Reason
                  </th>
                  <th
                    className={cn(
                      'border-0 border-b border-solid border-slate-200 bg-slate-50',
                      'px-3 py-2.5 text-left align-top text-slate-500',
                      MICRO_LABEL
                    )}
                  >
                    Ordered less than 6 months ago
                  </th>
                  <th
                    className={cn(
                      'border-0 border-b border-solid border-slate-200 bg-slate-50',
                      'px-3 py-2.5 text-left align-top text-slate-500',
                      MICRO_LABEL
                    )}
                  >
                    Ordered more than 6 months ago
                  </th>
                </tr>
              </thead>
              <tbody>
                {REASONS.map((reason, index) => {
                  // The last row drops its rule: the card's own padding is the gap under
                  // the table, and a border there reads as a row with nothing in it.
                  // Decided here rather than with a `last:` variant, which has to reach
                  // through the row to the cells that carry the border and does not.
                  const cell = cn(
                    'border-0 border-solid border-slate-200 px-3 py-2.5 align-top',
                    index < REASONS.length - 1 && 'border-b'
                  );

                  return (
                    <tr key={reason}>
                      <td className={cell}>{reason}</td>
                      <td className={cell}>
                        <span className="inline-block whitespace-nowrap rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-bold text-green-700">
                          Pro-rated refund
                        </span>
                      </td>
                      <td className={cell}>
                        <span className="inline-block whitespace-nowrap rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-800">
                          No refund
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h3 className={H3}>Ordered more than 6 months ago?</h3>
          <p>
            We can&apos;t refund the order, but your subscription continues through the end of its
            current term, and you can stop it from renewing at any time in{' '}
            <a href={discountLockUrl}>your account</a>.
          </p>

          <h3 className={H3}>Renewals through DiscountLock</h3>
          <p>
            A DiscountLock renewal is a new order with its own 6-month window, counted from the date
            the renewal was charged. We email you 30 days and 7 days before a renewal processes.
            Cancel before the process date for a full refund of that renewal, or within 6 months
            after it for a pro-rated refund.
          </p>

          <Note className="mt-4">
            <p className="mb-2 mt-0 text-slate-900">
              <b>Two exceptions to know about:</b>
            </p>
            <ul className="m-0 list-disc pl-5 marker:text-primary">
              <li className="mb-1.5">
                <b className="text-slate-900">Single-issue digital orders</b> are delivered to your
                Zinio or Magzter account within minutes and can only be cancelled from{' '}
                <a href={ordersUrl}>your dashboard</a> before delivery. Once delivered, they
                can&apos;t be refunded.
              </li>
              <li>
                <b className="text-slate-900">Reward orders</b>, meaning orders that earned cash
                back, a rebate, points, virtual currency or any other third-party reward, can be
                cancelled but aren&apos;t eligible for a refund for any reason.
              </li>
            </ul>
          </Note>
        </ContentCard>

        <ContentCard id="zinio" className={cn('prose', ANCHOR_OFFSET)}>
          <h2 className={H2}>ZINIO Unlimited</h2>
          <p className={TAGLINE}>Monthly plan, billed after any trial period.</p>
          <p>
            You can cancel anytime to stop future billing, and your access continues until the end
            of the period you&apos;ve paid for. Once a month has been billed, it isn&apos;t
            refundable.
          </p>
        </ContentCard>

        <ContentCard id="magzter" className={cn('prose', ANCHOR_OFFSET)}>
          <Pill>7-Day Guarantee</Pill>
          <h2 className={H2}>Magzter Gold</h2>
          <p className={TAGLINE}>Monthly and 1 to 3 year annual plans.</p>
          <p>
            Every Magzter Gold billing comes with a{' '}
            <b>7-Day No-Questions-Asked Refund Guarantee</b>: a full refund if you ask within 7 days
            of being billed.
          </p>
          <p>
            After 7 days, that billing isn&apos;t refundable, but you can cancel anytime to stop
            future charges. Your access continues until the end of the period you&apos;ve paid for.
          </p>
        </ContentCard>

        <ContentCard id="books" className={cn('prose', ANCHOR_OFFSET)}>
          <h2 className={H2}>Books &amp; other goods</h2>
          <p className={TAGLINE}>
            30-day returns, refunded to your original payment method.
          </p>

          <Note className="mb-4">
            <p className="m-0">
              <b className="text-slate-900">Arrived damaged, incomplete, or incorrect?</b>{' '}
              <a href={contactBooksUrl}>Contact Customer Care</a> <i>before</i> sending anything back and
              we&apos;ll make it right.
            </p>
          </Note>

          <p>
            Changed your mind? Return the item within <b>30 days of delivery</b> for a refund of the
            purchase price plus any taxes.
          </p>

          <Steps
            items={[
              <>
                <b className="text-slate-900">Let us know within 30 days of delivery</b> that
                you&apos;re returning the item.
              </>,
              <>
                <b className="text-slate-900">Ship it back with tracking.</b> You arrange and pay
                for return shipping. Items must be in original condition; shrink-wrapped items
                unopened and unused.
              </>,
              <>
                <b className="text-slate-900">We refund the purchase price plus tax</b> to your
                original payment method once the return arrives. Original shipping fees aren&apos;t
                refunded.
              </>,
            ]}
          />

          <p className="mb-0 mt-4 text-sm text-slate-500">
            Very large orders or repeat returns from the same customer may carry a processing fee.
          </p>
        </ContentCard>

        <ContentCard id="payment" className={cn('prose', ANCHOR_OFFSET)}>
          <h2 className={H2}>How refunds are paid</h2>
          <p className={TAGLINE}>
            Back to the payment method you used, once any bank dispute is closed.
          </p>

          <h3 className={cn(H3, 'mt-0')}>Original payment method only</h3>
          <p>
            We refund to the card or account you paid with. We process refunds within 48 hours; most
            banks post them within 5 to 7 business days.
          </p>

          <h3 className={H3}>If you&apos;ve opened a dispute with your bank or PayPal</h3>
          <p>
            Once a dispute is open, the order is locked on our side and we can&apos;t issue a refund
            or make changes until the dispute closes, which can take up to 80 days. Contacting us
            first is almost always faster: eligible refunds are processed within 48 hours.
          </p>
        </ContentCard>

        <ContentCard id="request" className={cn('prose', ANCHOR_OFFSET)}>
          <h2 className={H2}>How to request a refund</h2>

          <Steps
            items={[
              <>
                <b className="text-slate-900">Sign in to your account.</b> Most cancellations and
                refund requests are self-serve from <a href={ordersUrl}>My Orders</a>. No call
                needed.
              </>,
              <>
                <b className="text-slate-900">Or contact Customer Care.</b> Send us a message
                anytime. Have your order number or the email you ordered with handy. Phone support
                is reserved for existing customers; the number is on your credit card statement and
                in your order confirmation email.
              </>,
              <>
                <b className="text-slate-900">Get your money back.</b> Refunds go to your original
                payment method, usually within 5 to 7 business days.
              </>,
            ]}
          />

          <div className="not-prose mt-5 flex flex-wrap gap-3">
            <ButtonLink href={ordersUrl} className="normal-case tracking-normal">
              Go to My Orders
            </ButtonLink>
            <ButtonLink
              href={contactCancelUrl}
              className={cn(
                'border border-solid border-primary bg-white',
                'normal-case tracking-normal text-primary hover:bg-primary-50'
              )}
            >
              Contact Customer Care
            </ButtonLink>
          </div>

          <p className="mb-0 mt-4 text-sm text-slate-500">
            <b className="text-slate-900">Automated order support:</b> available 24/7 &nbsp;·&nbsp;{' '}
            <b className="text-slate-900">Live agents:</b> {liveAgentHours()}
          </p>
        </ContentCard>

        <div
          className={cn(
            'flex items-start gap-3.5 rounded-lg border border-solid p-5',
            'border-primary-100 bg-primary-50'
          )}
        >
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary text-white">
            <FontAwesomeIcon icon={faArrowRotateLeft as IconProp} aria-hidden />
          </span>
          <div>
            <p className="m-0 font-bold text-slate-900">Just want it to stop renewing?</p>
            <p className="m-0 mt-1 text-sm text-slate-700">
              Turn off DiscountLock for that subscription in{' '}
              <a href={discountLockUrl} className="text-primary underline">
                your account
              </a>
              . Future renewals stop, your subscription runs through the end of its current term,
              and nothing needs to be refunded.
            </p>
          </div>
        </div>

        {/* No `prose` on this one: the accordion is UI, not running copy, and prose would
            restyle its button and panel. The answers carry their own link colour. */}
        <ContentCard id="faqs" className={ANCHOR_OFFSET}>
          <h2 className="mb-4 mt-0 text-2xl font-extrabold tracking-tight text-slate-900">
            Frequently asked questions
          </h2>

          <div className="grid gap-2.5">
            <FaqAccordion
              defaultOpen
              question="Do I need a reason to get a refund?"
              answer={
                <>
                  No. Within the windows above (6 months for magazines, 30 days for books and goods,
                  7 days for Magzter Gold) any reason, or no reason, works.
                </>
              }
            />
            <FaqAccordion
              question="Why was I charged before my subscription expired?"
              answer={
                <>
                  DiscountLock renewals are timed from your order date, not the expiration date
                  printed on your label, so the publisher has time to continue service without a
                  gap. You won&apos;t get duplicate issues; the renewal simply extends your
                  expiration date. We email you 30 days and 7 days before any renewal.
                </>
              }
            />
            <FaqAccordion
              question="Can I cancel one magazine but keep the others?"
              answer={
                <>
                  Yes. Each subscription is its own order, so you can cancel or stop renewal on one
                  without affecting the rest.
                </>
              }
            />
            <FaqAccordion
              question="I ordered a gift. Who gets the refund?"
              answer={
                <>
                  Refunds always go back to the payment method used at checkout, so the purchaser
                  receives it, not the recipient.
                </>
              }
            />
            <FaqAccordion
              question="The subscription was for someone who has passed away."
              answer={
                <>
                  We&apos;re sorry for your loss.{' '}
                  <a href={contactBereavementUrl} className="text-primary underline">
                    Contact us
                  </a>{' '}
                  with the recipient&apos;s name and ZIP code and we&apos;ll stop renewals and
                  cancel the subscription. You won&apos;t need a login or order number.
                </>
              }
            />
            <FaqAccordion
              question="My magazine ceased publication. What happens?"
              answer={
                <>
                  Within 6 months of purchase, you can get a pro-rated refund of the unsent issues
                  on request. After 6 months, orders are final.
                </>
              }
            />
            <FaqAccordion
              question="Can I get a refund on an order that earned cash back or rewards?"
              answer={
                <>
                  No. Orders that received cash back, a rebate, points, virtual currency or any
                  other reward can be cancelled but aren&apos;t eligible for a refund.
                </>
              }
            />
            <FaqAccordion
              question="Where do I find my order number?"
              answer={
                <>
                  In your order confirmation email and under{' '}
                  <a href={ordersUrl} className="text-primary underline">
                    My Orders
                  </a>{' '}
                  in your account. The account number printed on your magazine label is the
                  publisher&apos;s number, not your DiscountMags order number.
                </>
              }
            />
          </div>
        </ContentCard>
      </div>
    </>
  );
}

RefundPolicy.layoutName = 'Layouts.Institutional';
